import { useTranslation } from 'react-i18next';
import type { MediaAsset } from '@ghostscreen/contracts';
import { formatMediaDurationBadge, contentKindLabel } from './buildContentLibrary';
import { getMediaOperatorStatus } from './mediaOperatorStatus';
import { MediaThumb } from './MediaThumb';

type ContentLibraryTileProps = {
  asset: MediaAsset;
  unused: boolean;
  onOpen: (asset: MediaAsset) => void;
};

export function ContentLibraryTile({ asset, unused, onOpen }: ContentLibraryTileProps) {
  const { t } = useTranslation('content');
  const status = getMediaOperatorStatus(asset);
  const duration = formatMediaDurationBadge(asset.durationSeconds);
  const busy = status.inProgress;
  const hasPreview = Boolean(asset.previewUrl);

  return (
    <button
      type="button"
      title={asset.originalFilename}
      onClick={() => onOpen(asset)}
      className="relative block aspect-square overflow-hidden rounded-md border-0 p-0 transition hover:brightness-110"
      style={{ background: '#1b1b1f', cursor: 'zoom-in', fontFamily: 'inherit' }}
      aria-label={t('tile.open', { name: asset.originalFilename })}
    >
      {hasPreview ? (
        <MediaThumb asset={asset} className="absolute inset-0 h-full w-full rounded-none border-0" />
      ) : (
        <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-2.5 text-[#CFCDC8]">
          <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-[#A3A29D]">
            {contentKindLabel(asset.kind)}
          </span>
          <span className="line-clamp-3 text-center text-[12.5px] font-semibold leading-tight">
            {asset.originalFilename}
          </span>
        </span>
      )}

      {asset.kind === 'video' && duration ? (
        <span className="absolute bottom-[7px] end-[7px] inline-flex items-center gap-1 rounded-md bg-black/60 px-[7px] py-[3px] text-[11.5px] font-bold text-white">
          <svg width="9" height="9" viewBox="0 0 24 24" fill="#FFFFFF" aria-hidden>
            <path d="M6 4l14 8-14 8z" />
          </svg>
          {duration}
        </span>
      ) : null}

      {unused ? (
        <span
          title={t('tile.notInPlaylist')}
          className="absolute start-[7px] top-[7px] h-[9px] w-[9px] rounded-full bg-[#E8B23C] shadow-[0_0_0_2px_rgba(0,0,0,0.45)]"
          aria-label={t('tile.notInPlaylist')}
        />
      ) : null}

      {busy ? (
        <span className="absolute inset-0 grid place-items-center bg-black/60 text-xs font-bold text-white">
          {t('tile.converting')}
        </span>
      ) : null}
    </button>
  );
}
